import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { Plus, MapPin } from "lucide-react";
import { useCreateVenue } from "@/hooks/useVenues";
import { toast } from "sonner";
import { AddressAutocomplete } from "./AddressAutocomplete";

interface CreateVenueDialogProps {
  /** Controlled open state. When provided, the default trigger button is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateVenueDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateVenueDialogProps = {}) {
  const createVenue = useCreateVenue();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) =>
    isControlled ? controlledOnOpenChange?.(next) : setUncontrolledOpen(next);
  const [form, setForm] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
  });

  const handleAddressSelect = (address: string, lat: number, lng: number) => {
    setForm((f) => ({
      ...f,
      address,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createVenue.mutateAsync({
        name: form.name,
        address: form.address,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      });
      toast.success("Lokalizacja dodana");
      setOpen(false);
      setForm({ name: "", address: "", latitude: "", longitude: "" });
    } catch (err) {
      toast.error("Nie udało się dodać lokalizacji", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      {!isControlled && (
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Dodaj lokalizację
        </Button>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Nowa lokalizacja"
        size="sm"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button type="submit" form="venue-create-form" disabled={createVenue.isPending}>
              {createVenue.isPending ? "Dodawanie..." : "Utwórz"}
            </Button>
          </>
        }
      >
        <form id="venue-create-form" onSubmit={handleSubmit}>
          <FormSection icon={MapPin} title="Lokalizacja">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="venue-name">Nazwa</Label>
                <Input
                  id="venue-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="np. Ostrołęckie Centrum Kultury"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-address">Adres</Label>
                <AddressAutocomplete
                  id="venue-address"
                  value={form.address}
                  onChange={(address) => setForm((f) => ({ ...f, address }))}
                  onSelect={handleAddressSelect}
                  placeholder="np. ul. Bogumła Zuga 1, Ostrołęka"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="venue-lat">Szerokość geo.</Label>
                  <Input
                    id="venue-lat"
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                    placeholder="53.0817"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue-lng">Długość geo.</Label>
                  <Input
                    id="venue-lng"
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                    placeholder="21.5667"
                  />
                </div>
              </div>
            </div>
          </FormSection>
        </form>
      </FormDialog>
    </>
  );
}
